import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, Send, CheckCircle2, Circle, ShieldCheck } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useWedding } from '../../context/WeddingContext'
import { useAuth } from '../../context/AuthContext'

const fmt = n => `₦${Number(n || 0).toLocaleString()}`

function Bubble({ from, children }) {
  const mine = from === 'me'
  return (
    <div style={{ display:'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
      <div style={{ maxWidth:'82%', background: mine ? '#C4826A' : '#fff', color: mine ? '#fff' : '#1A1A1A',
        border: mine ? 'none' : '1px solid #E5E0D8', borderRadius: mine ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
        padding:'0.7rem 0.95rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.88rem', lineHeight:1.55 }}>
        {children}
      </div>
    </div>
  )
}

export default function InquiryPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { wedding } = useWedding()
  const { user } = useAuth()
  const [vendor, setVendor] = useState(undefined)
  const [step, setStep] = useState('compose')   // compose | chat | tracker
  const [thread, setThread] = useState([])
  const [toast, setToast] = useState('')
  const [bookingId, setBookingId] = useState(null)
  const [feePaid, setFeePaid] = useState(false)
  const [paying, setPaying] = useState(false)
  const [protectSkipped, setProtectSkipped] = useState(false)
  const [walletBal, setWalletBal] = useState(0)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')
  const endRef = useRef(null)

  useEffect(() => {
    supabase.rpc('aisle_get_vendor', { p_id: id }).then(({ data }) => setVendor((data && data[0]) || null), () => setVendor(null))
  }, [id])
  useEffect(() => {
    if (!user?.id) return
    supabase.from('wallet').select('balance').eq('user_id', user.id).maybeSingle()
      .then(({ data }) => setWalletBal(Number(data?.balance || 0)), () => {})
  }, [user?.id])
  useEffect(() => { endRef.current?.scrollIntoView({ behavior:'smooth' }) }, [thread, step])
  // Prefill the inquiry message once the vendor loads.
  useEffect(() => {
    if (!vendor) return
    const ds = wedding?.wedding_date ? new Date(wedding.wedding_date).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : 'our wedding day'
    const cs = wedding?.city || 'our city'
    setMsg(`Hi ${vendor.business_name}, we're planning our wedding for ${ds} in ${cs}. We'd love to know more about your availability and packages.`)
  }, [vendor?.id]) // eslint-disable-line

  if (vendor === undefined) return <p style={{ textAlign:'center', color:'#BCBCBC', padding:'4rem', fontFamily:'Nunito Sans, sans-serif' }}>Loading…</p>
  if (vendor === null) return (
    <div style={{ textAlign:'center', padding:'4rem 1rem' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#9B9B9B', marginBottom:'1rem' }}>Vendor not found.</p>
      <button onClick={() => navigate('/dashboard/aisle')} className="btn-secondary">Back to The Aisle</button>
    </div>
  )

  const dateStr = wedding?.wedding_date ? new Date(wedding.wedding_date).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : 'our wedding day'
  const cityStr = wedding?.city || 'our city'
  const price = vendor.starting_price || 0
  const deposit = price ? Math.round(price * 0.4) : 70000
  const balance = price ? price - deposit : 105000
  const BOOKING_FEE = 3000

  // ── Send inquiry → scripted demo conversation ──
  function sendInquiry() {
    const text = msg.trim()
    if (!text) return
    setStep('chat')
    setThread([{ from:'me', text }])
    setToast(`Message sent! ${vendor.business_name} usually responds within 2 hours.`)
    setTimeout(() => setToast(''), 4000)
    // scripted vendor + couple exchange
    const script = [
      { from:'vendor', text:`Hi! Thank you so much for reaching out 💛 We're available on ${dateStr}. Our packages start at ${fmt(price)} and cover the full day — would you like the detailed breakdown?` },
      { from:'me',     text:`Yes please — and do you offer a payment plan?` },
      { from:'vendor', text:`Of course. A 40% deposit (${fmt(deposit)}) secures your date, and the balance (${fmt(balance)}) is due closer to the day. Shall we lock it in?` },
    ]
    script.forEach((m, i) => setTimeout(() => setThread(t => [...t, m]), 900 * (i + 1)))
  }

  // ── Confirm booking → create the booking row, show the tracker ──
  async function confirmBooking() {
    setError('')
    // Anti-double-booking: is the vendor free on our wedding date?
    if (wedding?.wedding_date) {
      const { data: busy } = await supabase.from('vendor_availability')
        .select('day').eq('vendor_id', vendor.id).eq('day', wedding.wedding_date).maybeSingle()
      if (busy) { setError(`${vendor.business_name} is already booked on ${dateStr}. Try another date or vendor.`); return }
    }
    try {
      const { data, error } = await supabase.from('vendor_bookings').insert({
        wedding_id: wedding.id, vendor_id: vendor.id, status: 'inquiry',
        deposit_amount: deposit, balance_amount: balance, booking_fee_amount: BOOKING_FEE,
        event_date: wedding?.wedding_date || null,
      }).select('id').single()
      if (error) throw error
      setBookingId(data.id)
      setStep('tracker')
    } catch (e) { setError(e.message || 'Could not create booking') }
  }

  // ── REAL ₦3,000 Okoiya booking fee (Paystack) ──
  function payBookingFee() {
    if (!window.PaystackPop) { setError('Payment script not loaded — please refresh.'); return }
    setError('')
    const ref = `okoiya_aisle_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    const handler = window.PaystackPop.setup({
      key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
      email: user.email,
      amount: BOOKING_FEE * 100,
      currency: 'NGN',
      ref,
      metadata: { booking_id: bookingId, custom_fields: [{ display_name:'Product', variable_name:'product', value:'Okoiya Aisle Booking Fee' }] },
      label: `Okoiya booking fee — ${vendor.business_name}`,
      callback: (response) => verifyFee(response.reference),
      onClose: () => setPaying(false),
    })
    setPaying(true)
    handler.openIframe()
  }
  // Pay the Okoiya Promise with referral wallet credit (universal Okoiya credit)
  async function payWithCredit() {
    setPaying(true); setError('')
    try {
      const { data, error } = await supabase.functions.invoke('aisle-booking-fee', { body: { booking_id: bookingId, method: 'credit' } })
      if (error || !data?.ok) throw new Error('Could not apply credit')
      setWalletBal(b => Math.max(0, b - BOOKING_FEE))
      setFeePaid(true)
    } catch { setError('We couldn’t apply your credit. Please try again.') }
    setPaying(false)
  }
  async function verifyFee(reference) {
    try {
      const { data, error } = await supabase.functions.invoke('aisle-booking-fee', { body: { booking_id: bookingId, reference } })
      if (error || !data?.ok) throw new Error('Verification failed')
      setFeePaid(true)
    } catch { setError('We couldn’t confirm that payment. If you were charged, contact support.') }
    setPaying(false)
  }

  return (
    <div style={{ maxWidth:600, margin:'0 auto', position:'relative' }}>
      <button onClick={() => navigate(`/dashboard/aisle/vendor/${id}`)}
        style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'none', border:'none', cursor:'pointer',
          fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', marginBottom:'1rem', padding:0 }}>
        <ChevronLeft size={16} /> {vendor.business_name}
      </button>

      {toast && (
        <div style={{ position:'fixed', top:80, left:'50%', transform:'translateX(-50%)', zIndex:60,
          background:'#1A1A1A', color:'#fff', borderRadius:100, padding:'0.6rem 1.1rem',
          fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', boxShadow:'0 8px 30px rgba(0,0,0,0.25)' }}>{toast}</div>
      )}

      {/* COMPOSE */}
      {step === 'compose' && (
        <div>
          <h1 style={{ fontFamily:'Gibs, serif', fontWeight:600, fontSize:'1.5rem', margin:'0 0 1rem' }}>Send an inquiry</h1>
          <textarea value={msg} onChange={e => setMsg(e.target.value)} rows={5}
            style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.9rem', border:'1px solid #E5E0D8', borderRadius:'1rem', padding:'0.9rem 1.1rem', outline:'none', boxSizing:'border-box', resize:'vertical', lineHeight:1.6 }} />
          <button onClick={sendInquiry}
            style={{ marginTop:'1rem', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', width:'100%',
              background:'#C4826A', color:'#fff', border:'none', borderRadius:100, padding:'0.875rem',
              fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', cursor:'pointer' }}>
            <Send size={16} /> Send
          </button>
        </div>
      )}

      {/* CHAT */}
      {step === 'chat' && (
        <div>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem', marginBottom:'1.25rem' }}>
            {thread.map((m, i) => <Bubble key={i} from={m.from}>{m.text}</Bubble>)}
            <div ref={endRef} />
          </div>
          {thread.length >= 4 && (
            <button onClick={confirmBooking}
              style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', width:'100%',
                background:'#9BAF9B', color:'#fff', border:'none', borderRadius:100, padding:'0.875rem',
                fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', cursor:'pointer' }}>
              <CheckCircle2 size={16} /> Confirm Booking
            </button>
          )}
        </div>
      )}

      {/* TRACKER */}
      {step === 'tracker' && (
        <div>
          <div className="card" style={{ padding:'1.5rem', maxWidth:420, margin:'0 auto' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.25rem' }}>
              <CheckCircle2 size={18} color="#9BAF9B" />
              <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.15rem', margin:0 }}>Request sent</h3>
            </div>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#6B6B6B', margin:'0 0 1.25rem' }}>Awaiting {vendor.business_name}'s response — you'll see it in your planning.</p>

            {/* Deposit (demo) */}
            <TrackerRow done={false}
              title={`Deposit — ${fmt(deposit)}`} sub="Pay directly to vendor"
              action={<a href="#" onClick={e=>e.preventDefault()} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', fontWeight:700, color:'#C4826A' }}>Vendor's Paystack link</a>}
              status="Awaiting payment" />

            {/* Balance (demo) */}
            <TrackerRow done={false}
              title={`Balance — ${fmt(balance)}`} sub="Due 7 days after delivery" status="Not yet due" />

            {/* Okoiya Promise — OPTIONAL deposit protection (REAL ₦3,000 charge) */}
            <div style={{ borderTop:'1px solid #F3F0EA', marginTop:'0.5rem', paddingTop:'0.875rem' }}>
              {feePaid ? (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.5rem' }}>
                  <div>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', margin:0, display:'flex', alignItems:'center', gap:'0.3rem' }}><ShieldCheck size={14} color="#3D8B5E"/> Okoiya Promise — active</p>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0.1rem 0 0' }}>Your deposit is protected against a no-show.</p>
                  </div>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'#3D8B5E' }}>Protected ✓</span>
                </div>
              ) : protectSkipped ? (
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:0 }}>
                  No problem — you can add the Okoiya Promise any time before your event.
                </p>
              ) : (
                <div style={{ background:'rgba(196,130,106,0.06)', border:'1px solid rgba(196,130,106,0.25)', borderRadius:'0.75rem', padding:'0.875rem' }}>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', margin:'0 0 0.2rem' }}>Add the Okoiya Promise — {fmt(BOOKING_FEE)} <span style={{ color:'#9B9B9B', fontWeight:400 }}>(optional)</span></p>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B', margin:'0 0 0.75rem', lineHeight:1.6 }}>
                    Protects your deposit (up to {fmt(deposit)}) if a verified vendor doesn't show up. It's optional — your booking is confirmed either way. <em>Not a quality guarantee.</em>
                  </p>
                  {walletBal >= BOOKING_FEE && (
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#3D8B5E', margin:'0 0 0.5rem', fontWeight:700 }}>
                      You have {fmt(walletBal)} referral credit — cover it for free.
                    </p>
                  )}
                  <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                    {walletBal >= BOOKING_FEE ? (
                      <button onClick={payWithCredit} disabled={paying}
                        style={{ display:'flex', alignItems:'center', gap:'0.4rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'#fff',
                          background: paying ? '#D4C4BC' : '#9BAF9B', border:'none', borderRadius:100, padding:'0.5rem 1.1rem', cursor: paying ? 'default' : 'pointer' }}>
                        <ShieldCheck size={14}/> {paying ? '…' : `Use ${fmt(BOOKING_FEE)} credit`}
                      </button>
                    ) : (
                      <button onClick={payBookingFee} disabled={paying}
                        style={{ display:'flex', alignItems:'center', gap:'0.4rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'#fff',
                          background: paying ? '#D4C4BC' : '#C4826A', border:'none', borderRadius:100, padding:'0.5rem 1.1rem', cursor: paying ? 'default' : 'pointer' }}>
                        <ShieldCheck size={14}/> {paying ? '…' : 'Add protection'}
                      </button>
                    )}
                    <button onClick={()=>setProtectSkipped(true)}
                      style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.78rem', color:'#6B6B6B', background:'none', border:'none', cursor:'pointer' }}>
                      Skip for now
                    </button>
                  </div>
                </div>
              )}
            </div>

            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'1rem 0 0', lineHeight:1.6 }}>
              Deposits and balances are paid directly to your vendor. Okoiya does not hold these funds — the Okoiya Promise is an optional protection, not a fee on your booking.
            </p>
          </div>

          {error && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#DC2626', textAlign:'center', margin:'1rem 0 0' }}>{error}</p>}

          <button onClick={() => navigate('/dashboard/planning')}
            style={{ display:'block', margin:'1.5rem auto 0', background:'none', border:'none', cursor:'pointer',
              fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#C4826A' }}>
            View in your planning →
          </button>
        </div>
      )}

      {error && step !== 'tracker' && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#DC2626', margin:'1rem 0 0' }}>{error}</p>}
    </div>
  )
}

function TrackerRow({ title, sub, action, status }) {
  return (
    <div style={{ display:'flex', gap:'0.6rem', alignItems:'flex-start', padding:'0.5rem 0' }}>
      <Circle size={14} color="#D1C9C0" style={{ marginTop:3, flexShrink:0 }} />
      <div style={{ flex:1, minWidth:0 }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', margin:0 }}>{title}</p>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0.1rem 0 0' }}>{sub}</p>
        {action && <div style={{ marginTop:'0.2rem' }}>{action}</div>}
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0.25rem 0 0' }}>Status: ○ {status}</p>
      </div>
    </div>
  )
}
